import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import SearchableSelect from '@/temu-platform/modules/products/components/SearchableSelect';
import {
  TEMU_INDIVIDUALLY_PACKED_OPTIONS,
  TEMU_PIECE_UNIT_OPTIONS,
  TEMU_SENSITIVE_LIST_OPTIONS,
  TEMU_SENSITIVE_YES_NO_OPTIONS,
  TEMU_SKU_CLASSIFICATION_OPTIONS,
  sensitiveListFromSlots,
  sensitiveSlotsFromList,
} from '@/temu-platform/utils/temuSkuPackage';
import {
  collectUniquePackageSizes,
  packageBatchHasAnyValue,
  packagePatchHasValue,
  parsePositiveNumberInput,
  validateDimsOrder,
} from '@/temu-platform/utils/temuPackageBatch';
import { showToast } from '@/utils/toast';
import i18n from '@/i18n';
import { useTranslation } from 'react-i18next';
import { localizeOptions } from '@/i18n/chartText';
import {
  PACKED_KEYS,
  PIECE_UNIT_KEYS,
  SENSITIVE_KEYS,
  SKU_CLASS_KEYS,
  SKU_PKG_TYPE_KEYS,
  YES_NO_KEYS,
} from '@/i18n/pkgOptionKeys';

function pkgText(key, fallback, options) {
  if (!i18n.isInitialized) return fallback;
  return i18n.t(key, { ns: 'workbench', defaultValue: fallback, ...(options || {}) }) || fallback;
}


export const PKG_BATCH_TITLES = {
  weight: '批量填写重量 / 货品净重（g）',
  dims: '批量填写体积（cm）',
  quantity: '批量填写 SKU 分类',
  packageType: '批量填写包装类型',
  sensitive: '批量填写敏感属性',
  accessories: '批量填写包装清单',
};

/** Temu 发品固定公制单位（不再提供 Shein 的 lb/Oz/Inch/Ft） */
export const WEIGHT_UNIT_OPTIONS = [
  { value: 'g', label: 'g' },
];

export const DIM_UNIT_OPTIONS = [
  { value: 'cm', label: 'cm' },
];

export const QUANTITY_UNIT_OPTIONS = TEMU_PIECE_UNIT_OPTIONS;

export const PACKAGE_TYPE_OPTIONS = [
  { value: '3', label: '硬包装' },
  { value: '2', label: '软包装+硬物' },
  { value: '1', label: '软包装+软物' },
  { value: '4', label: '抽真空' },
];

/**
 * @deprecated 请使用 TEMU_OUTER_PACKAGE_SHAPE_OPTIONS（货品外包装 ≠ SKU package_type）
 * 保留别名以免旧引用报错；取值已改为平台合规 0/1/2。
 */
export {
  TEMU_OUTER_PACKAGE_SHAPE_OPTIONS as PACKAGE_SHAPE_OPTIONS,
  TEMU_OUTER_PACKAGE_TYPE_OPTIONS as OUTER_PACKAGE_TYPE_OPTIONS,
} from '@/temu-platform/utils/temuOuterPackage';

function emptyRow() {
  return {
    weight: '',
    netWeight: '',
    length: '',
    width: '',
    height: '',
    quantity: '1',
    quantityUnit: '1',
    totalContains: '',
    skuClassification: '1',
    individuallyPacked: '',
    packageType: '',
    isSensitive: '0',
    sensitiveList: [],
    accessories: [{ vid: '', num: '', unitCode: 1, unitName: '件', thumbUrl: '', label: '' }],
  };
}

function emptyAccessory() {
  return { vid: '', num: '', unitCode: 1, unitName: '件', thumbUrl: '', label: '' };
}

function hasAnySku(skcList) {
  return (Array.isArray(skcList) ? skcList : []).some((skc) => (skc?.skuRows || []).length > 0);
}

function buildDimsPatch(row) {
  return {
    length: String(row?.length || '').trim(),
    width: String(row?.width || '').trim(),
    height: String(row?.height || '').trim(),
  };
}

function buildWeightPatch(row) {
  return {
    weight: String(row?.weight || '').trim(),
    netWeight: String(row?.netWeight || '').trim(),
  };
}

function buildQuantityPatch(row) {
  const qty = String(row?.quantity ?? '').trim();
  const unit = String(row?.quantityUnit || '1');
  const cls = String(row?.skuClassification || '1');
  const patch = {
    skuClassification: cls,
    quantity: qty,
    numberOfPieces: qty,
    quantityUnit: unit,
    pieceUnitCode: unit,
    totalContains: String(row?.totalContains ?? '').trim(),
  };
  if (cls === '2' || cls === '3') {
    patch.individuallyPacked = String(row?.individuallyPacked ?? '');
  } else {
    patch.individuallyPacked = '';
  }
  return patch;
}

function buildSensitivePatch(row) {
  const on = Number(row?.isSensitive) === 1;
  return {
    isSensitive: on ? 1 : 0,
    sensitiveList: on ? sensitiveListFromSlots(sensitiveSlotsFromList(row?.sensitiveList, 3)) : [],
  };
}

function buildAccessoriesPatch(row) {
  const list = (Array.isArray(row?.accessories) ? row.accessories : [])
    .map((item) => ({
      vid: item?.vid ? Number(item.vid) : '',
      num: String(item?.num ?? '').trim(),
      unitCode: item?.unitCode || 1,
      unitName: item?.unitName || '件',
      thumbUrl: item?.thumbUrl || '',
      label: item?.label || '',
    }));
  return {
    accessories: list.length ? list : [emptyAccessory()],
  };
}

function validateWeightValue(weight, netWeight, { netWeightRequired = false } = {}) {
  const parsed = parsePositiveNumberInput(weight);
  if (parsed.empty) return pkgText('publishPkg.fillWeight', '请填写重量');
  if (!parsed.ok) return parsed.message || pkgText('publishPkg.weightInvalid', '重量无效');
  const net = parsePositiveNumberInput(netWeight);
  if (netWeightRequired) {
    if (net.empty) return pkgText('publishPkg.fillNet', '请填写净重');
    if (!net.ok) return net.message || pkgText('publishPkg.netInvalid', '净重无效');
  } else if (!net.empty && !net.ok) {
    return net.message || pkgText('publishPkg.netInvalid', '净重无效');
  }
  return '';
}

function validateQuantityPatch(patch) {
  const cls = String(patch.skuClassification || '1');
  if (!['1', '2', '3'].includes(cls)) return pkgText('publishPkg.pickSkuClass', '请选择 SKU 分类');
  const qty = parsePositiveNumberInput(patch.quantity);
  if (qty.empty) return pkgText('publishPkg.fillPiece', '请填写单品数量');
  if (!qty.ok) return qty.message || pkgText('publishPkg.pieceInvalid', '单品数量无效');
  if (!Number.isInteger(qty.number)) return pkgText('publishPkg.pieceInt', '单品数量须为整数');
  const total = String(patch.totalContains ?? '').trim();
  if (total) {
    const t = parsePositiveNumberInput(total);
    if (!t.ok) return t.message || pkgText('publishPkg.totalInvalid', '共计内含无效');
    if (!Number.isInteger(t.number)) return pkgText('publishPkg.totalInt', '共计内含须为整数');
  }
  return '';
}

function validateSensitivePatch(patch) {
  if (Number(patch.isSensitive) !== 1) return '';
  const list = Array.isArray(patch.sensitiveList) ? patch.sensitiveList : [];
  if (!list.length) return pkgText('publishPkg.sensitiveNeed', '敏感属性为「是」时请至少选择 1 个类型');
  return '';
}

function validateAccessoriesPatch(patch) {
  const list = Array.isArray(patch.accessories) ? patch.accessories : [];
  const filled = list.filter((row) => Number(row?.vid) > 0 && Number(row?.num) > 0);
  if (!filled.length) return pkgText('publishPkg.accNeed', '请至少填写 1 项包装清单（物品 + 数量）');
  return '';
}

/** 仅重量支持「按尺码」；其他包装字段只按全部 SKU */
function ScopeTabs({ value, onChange, disabled }) {
  return (
    <div className="tpo-pkg-batch__scope" role="tablist" aria-label={pkgText('publishPkg.scope', '应用范围')}>
      <button
        type="button"
        role="tab"
        aria-selected={value === 'all'}
        className={`tpo-pkg-batch__scope-btn${value === 'all' ? ' is-active' : ''}`}
        disabled={disabled}
        onClick={() => onChange('all')}
      >
        {pkgText('publishPkg.allSku', '全部 SKU')}
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={value === 'bySize'}
        className={`tpo-pkg-batch__scope-btn${value === 'bySize' ? ' is-active' : ''}`}
        disabled={disabled}
        onClick={() => onChange('bySize')}
      >
        {pkgText('publishPkg.bySize', '按尺码')}
      </button>
    </div>
  );
}

function DimsFields({ row, onChange, inputRef, onEnter }) {
  return (
    <div className="tpo-pkg-batch__dims">
      <label className="tpo-pkg-batch__field">
        <span className="tpo-pkg-batch__label">{pkgText('publishPkg.longest', fixedUi("最长边"))}</span>
        <div className="tpo-pkg-batch__control">
          <input
            ref={inputRef}
            className="tpo-input"
            value={row.length}
            inputMode="decimal"
            placeholder={pkgText('publishPkg.input', fixedUi("请输入"))}
            onChange={(e) => onChange({ length: e.target.value })}
          />
          <em>cm</em>
        </div>
      </label>
      <label className="tpo-pkg-batch__field">
        <span className="tpo-pkg-batch__label">{pkgText('publishPkg.mid', fixedUi("次长边"))}</span>
        <div className="tpo-pkg-batch__control">
          <input
            className="tpo-input"
            value={row.width}
            inputMode="decimal"
            placeholder={pkgText('publishPkg.input', fixedUi("请输入"))}
            onChange={(e) => onChange({ width: e.target.value })}
          />
          <em>cm</em>
        </div>
      </label>
      <label className="tpo-pkg-batch__field">
        <span className="tpo-pkg-batch__label">{pkgText('publishPkg.shortest', fixedUi("最短边"))}</span>
        <div className="tpo-pkg-batch__control">
          <input
            className="tpo-input"
            value={row.height}
            inputMode="decimal"
            placeholder={pkgText('publishPkg.input', fixedUi("请输入"))}
            onChange={(e) => onChange({ height: e.target.value })}
            onKeyDown={onEnter}
          />
          <em>cm</em>
        </div>
      </label>
    </div>
  );
}

function WeightFields({ row, onChange, inputRef, onEnter, netWeightRequired = false }) {
  return (
    <div className="tpo-pkg-batch__weight">
      <label className="tpo-pkg-batch__field">
        <span className="tpo-pkg-batch__label">{pkgText('publishPkg.weight', fixedUi("重量"))}</span>
        <div className="tpo-pkg-batch__control">
          <input
            ref={inputRef}
            className="tpo-input"
            value={row.weight}
            inputMode="decimal"
            placeholder={pkgText('publishPkg.like200', '如 200')}
            onChange={(e) => onChange({ weight: e.target.value })}
            onKeyDown={onEnter}
          />
          <em>g</em>
        </div>
      </label>
      <label className="tpo-pkg-batch__field">
        <span className="tpo-pkg-batch__label">{netWeightRequired ? pkgText('publishPkg.netReqLabel', fixedUi("货品净重（必填）")) : pkgText('publishPkg.netOptLabel', fixedUi("货品净重（选填）"))}</span>
        <div className="tpo-pkg-batch__control">
          <input
            className="tpo-input"
            value={row.netWeight || ''}
            inputMode="decimal"
            placeholder={netWeightRequired ? '如 180' : '选填，如 180'}
            onChange={(e) => onChange({ netWeight: e.target.value })}
            onKeyDown={onEnter}
          />
          <em>g</em>
        </div>
      </label>
    </div>
  );
}

function QuantityFields({ row, onChange, inputRef, onEnter }) {
  const cls = String(row.skuClassification || '1');
  const isMulti = cls === '2' || cls === '3';
  return (
    <div className="tpo-pkg-batch__quantity">
      <label className="tpo-pkg-batch__field">
        <span className="tpo-pkg-batch__label">{pkgText('publishPkg.skuClass', fixedUi("SKU 分类"))}</span>
        <div className="tpo-pkg-batch__control tpo-pkg-batch__control--grow">
          <SearchableSelect
            value={cls}
            allowClear={false}
            onChange={(v) => {
              const next = String(v || '1');
              onChange({
                skuClassification: next,
                ...(next === '1' ? { individuallyPacked: '' } : {}),
              });
            }}
            options={localizeOptions(TEMU_SKU_CLASSIFICATION_OPTIONS, SKU_CLASS_KEYS)}
          />
        </div>
      </label>
      <label className="tpo-pkg-batch__field">
        <span className="tpo-pkg-batch__label">{pkgText('publishPkg.pieceQty', fixedUi("单品数量"))}</span>
        <div className="tpo-pkg-batch__control">
          <input
            ref={inputRef}
            className="tpo-input tpo-pkg-inline__input--qty"
            value={row.quantity}
            inputMode="numeric"
            placeholder={pkgText('publishPkg.input', fixedUi("请输入"))}
            onChange={(e) => onChange({ quantity: e.target.value })}
            onKeyDown={onEnter}
          />
          <SearchableSelect
            className="tpo-select--unit"
            value={String(row.quantityUnit || '1')}
            allowClear={false}
            onChange={(v) => onChange({ quantityUnit: String(v || '1') })}
            options={localizeOptions(QUANTITY_UNIT_OPTIONS, PIECE_UNIT_KEYS)}
          />
        </div>
      </label>
      <label className="tpo-pkg-batch__field">
        <span className="tpo-pkg-batch__label">{pkgText('publishPkg.totalContains', fixedUi("共计内含"))}</span>
        <div className="tpo-pkg-batch__control">
          <input
            className="tpo-input tpo-pkg-inline__input--qty"
            value={row.totalContains}
            inputMode="numeric"
            placeholder={pkgText('publishPkg.optionalPh', '选填')}
            onChange={(e) => onChange({ totalContains: e.target.value })}
            onKeyDown={onEnter}
          />
          <em>
            {QUANTITY_UNIT_OPTIONS.find((o) => o.value === String(row.quantityUnit || '1'))?.label || pkgText('publishPkg.piece', '件')}
          </em>
        </div>
      </label>
      {isMulti ? (
        <label className="tpo-pkg-batch__field">
          <span className="tpo-pkg-batch__label">{pkgText('publishPkg.indep', fixedUi("独立包装"))}</span>
          <div className="tpo-pkg-batch__control tpo-pkg-batch__control--grow">
            <SearchableSelect
              value={row.individuallyPacked === '0' || row.individuallyPacked === '1'
                ? String(row.individuallyPacked)
                : ''}
              allowClear
              placeholder={pkgText('shop.pleaseSelect', fixedUi("请选择"))}
              onChange={(v) => onChange({
                individuallyPacked: v == null || v === '' ? '' : String(v),
              })}
              options={localizeOptions(TEMU_INDIVIDUALLY_PACKED_OPTIONS, PACKED_KEYS)}
            />
          </div>
        </label>
      ) : null}
    </div>
  );
}

function SensitiveFields({ row, onChange }) {
  const on = Number(row.isSensitive) === 1;
  const slots = sensitiveSlotsFromList(row.sensitiveList, 3);
  return (
    <div className="tpo-pkg-batch__sensitive">
      <label className="tpo-pkg-batch__field">
        <span className="tpo-pkg-batch__label">{pkgText('publishPkg.sensitiveQ', fixedUi("是否敏感"))}</span>
        <div className="tpo-pkg-batch__control tpo-pkg-batch__control--grow">
          <SearchableSelect
            value={String(row.isSensitive ?? 0)}
            allowClear={false}
            onChange={(v) => onChange({
              isSensitive: Number(v) === 1 ? '1' : '0',
              ...(Number(v) !== 1 ? { sensitiveList: [] } : {}),
            })}
            options={localizeOptions(TEMU_SENSITIVE_YES_NO_OPTIONS, YES_NO_KEYS)}
          />
        </div>
      </label>
      {on ? (
        <div className="tpo-pkg-batch__sensitive-types">
          {slots.map((slotVal, slotIdx) => (
            <label key={`sens-batch-${slotIdx}`} className="tpo-pkg-batch__field">
              <span className="tpo-pkg-batch__label">{pkgText('publishPkg.typeN', `类型 ${slotIdx + 1}`, { n: slotIdx + 1 })}</span>
              <div className="tpo-pkg-batch__control tpo-pkg-batch__control--grow">
                <SearchableSelect
                  value={slotVal}
                  allowClear
                  placeholder={pkgText('shop.pleaseSelect', fixedUi("请选择"))}
                  onChange={(v) => {
                    const nextSlots = [...slots];
                    nextSlots[slotIdx] = v == null ? '' : String(v);
                    onChange({ sensitiveList: sensitiveListFromSlots(nextSlots) });
                  }}
                  options={localizeOptions(TEMU_SENSITIVE_LIST_OPTIONS, SENSITIVE_KEYS)}
                />
              </div>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function AccessoriesFields({ row, onChange, accessoryOptions = [] }) {
  const list = Array.isArray(row.accessories) && row.accessories.length
    ? row.accessories
    : [emptyAccessory()];
  return (
    <div className="tpo-pkg-batch__accessories">
      {list.map((item, idx) => (
        <div key={`acc-batch-${idx}`} className="tpo-pkg-accessories__row">
          <SearchableSelect
            value={item.vid ? String(item.vid) : ''}
            allowClear
            placeholder={accessoryOptions.length ? pkgText('publishPkg.accSearch', '可选择或搜索物品') : pkgText('publishPkg.accNoneLoaded', '暂无物品类型，请先加载包装清单')}
            disabled={!accessoryOptions.length}
            onChange={(v) => {
              const opt = accessoryOptions.find((o) => o.value === String(v));
              const next = list.map((rowItem, i) => (
                i === idx
                  ? {
                    ...rowItem,
                    vid: v ? Number(v) : '',
                    label: opt?.label || '',
                    unitCode: opt?.unitCode || rowItem.unitCode || 1,
                    unitName: opt?.unitName || rowItem.unitName || '件',
                    thumbUrl: opt?.thumbUrl || rowItem.thumbUrl || '',
                  }
                  : rowItem
              ));
              onChange({ accessories: next });
            }}
            options={accessoryOptions}
          />
          <input
            className="tpo-input tpo-pkg-inline__input--qty"
            value={item.num ?? ''}
            inputMode="numeric"
            placeholder={pkgText('publishPkg.qty', fixedUi("数量"))}
            onChange={(e) => {
              const next = list.map((rowItem, i) => (
                i === idx ? { ...rowItem, num: e.target.value } : rowItem
              ));
              onChange({ accessories: next });
            }}
          />
          <span className="tpo-pkg-inline__unit">
            {item.unitName
              || accessoryOptions.find((o) => o.value === String(item.vid))?.unitName
              || pkgText('publishPkg.piece', '件')}
          </span>
          <button
            type="button"
            className="tpo-pkg-accessories__del"
            title={pkgText('publishPkg.deleteItem', '删除物品')}
            aria-label={pkgText('publishPkg.deleteItem', '删除物品')}
            onClick={() => {
              const next = list.filter((_, i) => i !== idx);
              onChange({ accessories: next.length ? next : [emptyAccessory()] });
            }}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
              <path
                fill="currentColor"
                d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v10h-2V9zm4 0h2v10h-2V9zM7 9h2v10H7V9zm-1 13h12a1 1 0 0 0 1-1V7H5v14a1 1 0 0 0 1 1z"
              />
            </svg>
          </button>
        </div>
      ))}
      <button
        type="button"
        className="tpo-pkg-accessories__add"
        disabled={list.length >= 20}
        onClick={() => onChange({ accessories: [...list, emptyAccessory()] })}
      >
        {pkgText('publishPkg.add', '+ 添加')}
      </button>
    </div>
  );
}

export default function PackageBatchModal({
  mode,
  skcList = [],
  sizeAttr = null,
  accessoryOptions = [],
  onClose,
  onConfirm,
  weightUnit: initialWeightUnit = 'g',
  dimUnit: initialDimUnit = 'cm',
  /** 叶子类目命中平台「货品净重必填」清单时为 true */
  netWeightRequired = false,
}) {
  useTranslation('workbench');
  const sizes = useMemo(() => collectUniquePackageSizes(skcList, sizeAttr), [skcList, sizeAttr]);
  const canApply = hasAnySku(skcList);
  const isWeight = mode === 'weight';
  const isQuantity = mode === 'quantity';
  const isPackageType = mode === 'packageType';
  const isSensitive = mode === 'sensitive';
  const isAccessories = mode === 'accessories';
  const isDims = mode === 'dims'
    || (!isWeight && !isQuantity && !isPackageType && !isSensitive && !isAccessories);

  const [applyScope, setApplyScope] = useState('all');
  const [error, setError] = useState('');
  const [weightUnit] = useState(initialWeightUnit || 'g');
  const [dimUnit] = useState(initialDimUnit || 'cm');
  const [rows, setRows] = useState(() => {
    const init = {};
    sizes.forEach((size) => {
      init[size.key] = { ...emptyRow(), ...(size.seed || {}) };
    });
    return init;
  });
  const [sharedRow, setSharedRow] = useState(() => ({
    ...emptyRow(),
    ...(sizes[0]?.seed || {}),
  }));
  const inputRef = useRef(null);

  const sizeSignature = useMemo(
    () => sizes.map((s) => `${s.key}:${s.label}`).join('|'),
    [sizes],
  );

  useEffect(() => {
    setApplyScope('all');
    setError('');
    const nextRows = {};
    sizes.forEach((size) => {
      nextRows[size.key] = { ...emptyRow(), ...(size.seed || {}) };
    });
    setRows(nextRows);
    setSharedRow({ ...emptyRow(), ...(sizes[0]?.seed || {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, sizeSignature]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [mode]);

  const updateRow = (key, patch) => {
    setError('');
    setRows((prev) => ({
      ...prev,
      [key]: { ...emptyRow(), ...(prev[key] || {}), ...patch },
    }));
  };

  const updateShared = (patch) => {
    setError('');
    setSharedRow((prev) => ({ ...emptyRow(), ...prev, ...patch }));
  };

  const fillSharedToSizes = () => {
    if (!sizes.length) return;
    setRows((prev) => {
      const next = { ...prev };
      sizes.forEach((size) => {
        next[size.key] = { ...emptyRow(), ...(prev[size.key] || {}), ...sharedRow };
      });
      return next;
    });
    showToast(pkgText('publishPkg.filledSizes', '已将上方值填充到各尺码行'), 'success');
  };

  const buildPatchForMode = (row) => {
    if (isPackageType) return { packageType: String(row.packageType || '') };
    if (isDims) return buildDimsPatch(row);
    if (isWeight) return buildWeightPatch(row);
    if (isSensitive) return buildSensitivePatch(row);
    if (isAccessories) return buildAccessoriesPatch(row);
    return buildQuantityPatch(row);
  };

  const validatePatchForMode = (patch) => {
    if (isPackageType) {
      return packagePatchHasValue(patch) ? '' : pkgText('publishPkg.pickPkgType', '请选择包装类型');
    }
    if (isDims) return validateDimsOrder(patch.length, patch.width, patch.height);
    if (isWeight) return validateWeightValue(patch.weight, patch.netWeight, { netWeightRequired });
    if (isSensitive) return validateSensitivePatch(patch);
    if (isAccessories) return validateAccessoriesPatch(patch);
    return validateQuantityPatch(patch);
  };

  const allowBySize = isWeight;
  const useSharedForm = !allowBySize || applyScope === 'all';

  const submit = () => {
    if (!canApply) {
      setError(pkgText('publishPkg.pickSpecColors', '请先在规格中选择颜色与尺码'));
      showToast(pkgText('publishPkg.pickSizeFirst', fixedUi("请先选择规格尺码")), 'warning');
      return;
    }

    if (useSharedForm) {
      const patch = buildPatchForMode(sharedRow);
      const err = validatePatchForMode(patch);
      if (err) {
        setError(err);
        showToast(err, 'warning');
        return;
      }
      onConfirm?.({
        applyAll: true,
        patch,
        weightUnit: String(weightUnit || 'g'),
        dimUnit: String(dimUnit || 'cm'),
      });
      return;
    }

    if (!sizes.length) {
      setError(pkgText('publishPkg.pickSpecSize', '请先在规格中选择尺码'));
      showToast(pkgText('publishPkg.pickSizeFirst', fixedUi("请先选择规格尺码")), 'warning');
      return;
    }

    const bySize = {};
    let firstError = '';
    let filled = 0;
    sizes.forEach((size) => {
      const row = rows[size.key] || emptyRow();
      const patch = buildPatchForMode(row);
      if (!packagePatchHasValue(patch)) return;
      const err = validatePatchForMode(patch);
      if (err) {
        if (!firstError) firstError = `${size.label}：${err}`;
        return;
      }
      bySize[size.key] = patch;
      filled += 1;
    });

    if (firstError) {
      setError(firstError);
      showToast(firstError, 'warning');
      return;
    }
    if (!filled || !packageBatchHasAnyValue(bySize)) {
      const msg = pkgText('publishPkg.fillOneSize', '请至少填写一个尺码的值');
      setError(msg);
      showToast(msg, 'warning');
      return;
    }

    onConfirm?.({
      bySize,
      weightUnit: String(weightUnit || 'g'),
      dimUnit: String(dimUnit || 'cm'),
    });
  };

  const enterSubmit = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  const title = pkgText({
    weight: 'publishPkg.titleWeightNet',
    dims: 'publishPkg.titleDimsCm',
    quantity: 'publishPkg.titleSkuClass',
    packageType: 'publishPkg.titlePkgType',
    sensitive: 'publishPkg.titleSensitive',
    accessories: 'publishPkg.titleAcc',
  }[mode] || 'publishPkg.batchFill', PKG_BATCH_TITLES[mode] || fixedUi("批量填写"));

  const renderModeFields = (row, onChange, withRef) => {
    if (isPackageType) {
      return (
        <SearchableSelect
          value={row.packageType}
          allowClear
          placeholder={pkgText('publishPkg.pickPkgType', '请选择包装类型')}
          onChange={(v) => onChange({ packageType: v == null ? '' : String(v) })}
          options={localizeOptions(PACKAGE_TYPE_OPTIONS, SKU_PKG_TYPE_KEYS)}
        />
      );
    }
    if (isDims) {
      return (
        <DimsFields
          row={row}
          onChange={onChange}
          inputRef={withRef ? inputRef : undefined}
          onEnter={enterSubmit}
        />
      );
    }
    if (isWeight) {
      return (
        <WeightFields
          row={row}
          onChange={onChange}
          inputRef={withRef ? inputRef : undefined}
          onEnter={enterSubmit}
          netWeightRequired={netWeightRequired}
        />
      );
    }
    if (isSensitive) {
      return <SensitiveFields row={row} onChange={onChange} />;
    }
    if (isAccessories) {
      return (
        <AccessoriesFields
          row={row}
          onChange={onChange}
          accessoryOptions={accessoryOptions}
        />
      );
    }
    return (
      <QuantityFields
        row={row}
        onChange={onChange}
        inputRef={withRef ? inputRef : undefined}
        onEnter={enterSubmit}
      />
    );
  };

  return (
    <div className="tpo-batch-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div
        className={[
          'tpo-batch-modal__panel',
          'tpo-batch-modal__panel--pkg-size',
          isDims ? 'tpo-batch-modal__panel--pkg-dims' : '',
          isQuantity ? 'tpo-batch-modal__panel--pkg-quantity' : '',
          isAccessories ? 'tpo-batch-modal__panel--pkg-accessories' : '',
          useSharedForm ? 'tpo-batch-modal__panel--pkg-shared' : '',
        ].filter(Boolean).join(' ')}
      >
        <div className="tpo-batch-modal__head">
          <strong>{title}</strong>
          <button type="button" className="tpo-batch-modal__close" aria-label={pkgText('moduleShell.close', fixedUi("关闭"))} onClick={onClose}>×</button>
        </div>
        <div className="tpo-batch-modal__body">
          {allowBySize ? (
            <ScopeTabs
              value={applyScope}
              onChange={(next) => {
                setApplyScope(next);
                setError('');
              }}
              disabled={!canApply}
            />
          ) : null}

          {!canApply ? (
            <p className="tpo-pkg-batch__error" role="alert">{pkgText('publishPkg.pickColorSize', fixedUi("请先选择规格颜色与尺码"))}</p>
          ) : useSharedForm ? (
            renderModeFields(sharedRow, updateShared, true)
          ) : (
            <>
              {!sizes.length ? (
                <p className="tpo-pkg-batch__error" role="alert">{pkgText('publishPkg.pickSizeFirst', fixedUi("请先选择规格尺码"))}</p>
              ) : (
                <>
                  <div className="tpo-pkg-batch__toolbar">
                    <span className="tpo-pkg-batch__toolbar-hint">{pkgText('publishPkg.fillTemplate', fixedUi("可先填一套模板再同步到各尺码"))}</span>
                    <button
                      type="button"
                      className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                      onClick={fillSharedToSizes}
                    >
                      {pkgText('publishPkg.fillToSizes', '填充到各尺码')}
                    </button>
                  </div>
                  <div className="tpo-pkg-batch__template">
                    {renderModeFields(sharedRow, updateShared, true)}
                  </div>
                  <div className="tpo-pkg-size-rows">
                    {sizes.map((size) => {
                      const row = rows[size.key] || emptyRow();
                      return (
                        <div key={size.key} className="tpo-pkg-size-row tpo-pkg-size-row--rich">
                          <span className="tpo-pkg-size-row__name" title={size.label}>{size.label}</span>
                          <div className="tpo-pkg-size-row__fields">
                            {renderModeFields(row, (patch) => updateRow(size.key, patch), false)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}

          {error ? (
            <p className="tpo-pkg-batch__error" role="alert">{error}</p>
          ) : null}
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={onClose}>{pkgText('shop.cancel', fixedUi("取消"))}</button>
          <button
            type="button"
            className="tpo-btn tpo-btn--primary tpo-btn--sm"
            onClick={submit}
            disabled={!canApply || (!useSharedForm && !sizes.length)}
          >
            {pkgText('moduleShell.confirmOk', '确定')}
          </button>
        </div>
      </div>
    </div>
  );
}
